import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { setJuthoorBudget } from '@/lib/api/juthoor';
import { t } from '@/lib/i18n/messages';
import { formatNumber } from '@/lib/utils/format';
import type { Locale } from '@/types/auth';
import type { JuthoorInitiative } from '@/types/juthoor';

interface JuthoorSetBudgetModalProps {
  initiative: JuthoorInitiative;
  locale: Locale;
  onSuccess: () => void;
  onClose: () => void;
}

export function JuthoorSetBudgetModal({
  initiative,
  locale,
  onSuccess,
  onClose,
}: JuthoorSetBudgetModalProps) {
  const isRTL = locale === 'ar';
  const initialBudget = initiative.juthoor.budget != null && initiative.juthoor.budget > 0
    ? String(initiative.juthoor.budget)
    : '';

  const [amount, setAmount] = useState(initialBudget);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const numericAmount = parseFloat(amount) || 0;
  const isValid = numericAmount > 0;

  // Helpful suggestion: total trees × reward per tree
  const suggested =
    initiative.juthoor.total_trees * initiative.juthoor.reward_per_tree;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await setJuthoorBudget(initiative.id, numericAmount);
      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || t(locale, 'error'));
      }
    } catch {
      setError(t(locale, 'error'));
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="juthoor-budget-modal-title"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="bg-card w-full max-w-[460px] rounded-3xl shadow-2xl overflow-hidden">
        <div className="p-7 space-y-6">
          <div>
            <h2
              id="juthoor-budget-modal-title"
              className="text-xl font-bold text-foreground"
            >
              {isRTL ? 'تعيين ميزانية المبادرة' : 'Set Initiative Budget'}
            </h2>
            <p className="mt-1 text-sm text-foreground-secondary">
              {isRTL
                ? 'يجب تعيين الميزانية قبل إرسال المبادرة إلى الأمانة للموافقة.'
                : 'Budget must be set before submitting the initiative to the municipality.'}
            </p>
          </div>

          {/* Reference: total trees × reward per tree */}
          <div className="rounded-2xl border border-neutral-200 dark:border-gray-700 bg-neutral-50 dark:bg-gray-800/50 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground-secondary uppercase tracking-wider">
                {isRTL ? 'الحدّ الأدنى المُقترَح' : 'Suggested minimum'}
              </span>
              <button
                type="button"
                onClick={() => setAmount(String(suggested))}
                className="text-[11px] font-bold text-primary hover:underline"
              >
                {isRTL ? 'استخدم' : 'Use'}
              </button>
            </div>
            <p className="mt-1 text-sm font-semibold text-foreground tabular-nums">
              {formatNumber(suggested, locale)} NC
            </p>
            <p className="mt-1 text-[11px] text-foreground-secondary">
              {isRTL
                ? `${formatNumber(initiative.juthoor.total_trees, locale)} شجرة × ${formatNumber(initiative.juthoor.reward_per_tree, locale)} GP لكل شجرة`
                : `${formatNumber(initiative.juthoor.total_trees, locale)} trees × ${formatNumber(initiative.juthoor.reward_per_tree, locale)} GP per tree`}
            </p>
          </div>

          {/* Amount input */}
          <div>
            <label
              htmlFor="juthoor-budget-amount"
              className="block text-sm font-medium text-foreground-secondary mb-2"
            >
              {isRTL ? 'الميزانية (NC)' : 'Budget (NC)'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <input
              ref={inputRef}
              id="juthoor-budget-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              dir="ltr"
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-4 py-3 bg-card border border-gray-200 dark:border-gray-700 rounded-xl text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none text-lg tabular-nums"
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-6 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-foreground font-medium hover:bg-muted transition-all disabled:opacity-50"
            >
              {t(locale, 'cancelAction')}
            </button>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!isValid || submitting}
              className="flex-1 px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  {t(locale, 'loading')}
                </>
              ) : (
                <>{isRTL ? 'حفظ الميزانية' : 'Save Budget'}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
